import { useCallback, useState } from "react";
import { useAction } from "convex/react";
import { useTranslation } from "@/packages/utils";
import { Check, X } from "lucide-react";
import { Button, Badge } from "@/components/ui";
import { api } from "convex/_generated/api";
import {
  POLAR_PRODUCT_IDS,
  PRICING,
  TIER_ORDER,
  TIER_FEATURES,
} from "@/packages/features-billing";
import type { BillingInterval, PaidTier } from "@/packages/features-billing";
import type { BillingTier } from "@/packages/domains/billing";
import { PlanChangeDialog } from "./PlanChangeDialog";

const PAID_TIERS: PaidTier[] = ["essentials", "professional", "unlimited"];

interface PricingTableProps {
  currentTier: BillingTier;
  billingInterval: "monthly" | "yearly" | null;
}

export function PricingTable({
  currentTier,
  billingInterval,
}: PricingTableProps) {
  const [interval, setInterval] = useState<BillingInterval>(
    billingInterval ?? "yearly",
  );
  const [pendingTier, setPendingTier] = useState<PaidTier | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const generateCheckoutLink = useAction(api.polar.generateCheckoutLink);
  const generatePortalUrl = useAction(api.polar.generateCustomerPortalUrl);

  const isMonthlyUser = billingInterval === "monthly";
  const showSavingsHint = isMonthlyUser && interval === "monthly";

  const handlePlanChangeRequest = useCallback((tier: PaidTier) => {
    setPendingTier(tier);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!pendingTier) return;

    const effectiveCurrent =
      currentTier === "free" ? "essentials" : currentTier;
    const isUpgrade = TIER_ORDER[pendingTier] > TIER_ORDER[effectiveCurrent];

    setConfirmLoading(true);
    try {
      if (isUpgrade) {
        const productId = POLAR_PRODUCT_IDS[pendingTier];
        if (!productId) return;

        const { url } = await generateCheckoutLink({
          productIds: [productId],
          origin: window.location.origin,
          successUrl: window.location.href,
        });
        window.open(url, "_blank");
      } else {
        const { url } = await generatePortalUrl({});
        window.open(url, "_blank");
      }
    } finally {
      setConfirmLoading(false);
      setPendingTier(null);
    }
  }, [pendingTier, currentTier, generateCheckoutLink, generatePortalUrl]);

  const handleClose = useCallback(() => {
    setPendingTier(null);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-4">
        <BillingToggle interval={interval} onChange={setInterval} />
        <SavingsHint visible={showSavingsHint} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PAID_TIERS.map((tier) => (
          <PlanColumn
            key={tier}
            tier={tier}
            interval={interval}
            currentTier={currentTier}
            billingInterval={billingInterval}
            onPlanChangeRequest={handlePlanChangeRequest}
          />
        ))}
      </div>

      {pendingTier && (
        <PlanChangeDialog
          open={true}
          onClose={handleClose}
          onConfirm={handleConfirm}
          loading={confirmLoading}
          currentTier={currentTier}
          targetTier={pendingTier}
        />
      )}
    </div>
  );
}

function BillingToggle({
  interval,
  onChange,
}: {
  interval: BillingInterval;
  onChange: (interval: BillingInterval) => void;
}) {
  const { t } = useTranslation(["settings"]);

  return (
    <div className="inline-flex items-center rounded-full border border-border bg-page p-1">
      <button
        type="button"
        onClick={() => onChange("monthly")}
        className={`px-5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
          interval === "monthly"
            ? "bg-card text-primary shadow-sm"
            : "text-secondary hover:text-primary"
        }`}
      >
        {t("settings:billing.pricing.toggleMonthly")}
      </button>
      <button
        type="button"
        onClick={() => onChange("yearly")}
        className={`px-5 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
          interval === "yearly"
            ? "bg-card text-primary shadow-sm"
            : "text-secondary hover:text-primary"
        }`}
      >
        {t("settings:billing.pricing.toggleYearly")}
      </button>
    </div>
  );
}

function SavingsHint({ visible }: { visible: boolean }) {
  const { t } = useTranslation(["settings"]);

  return (
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${
        visible ? "max-h-12 opacity-100" : "max-h-0 opacity-0"
      }`}
    >
      <div className="rounded-full bg-accent-soft/50 border border-accent/20 px-4 py-2 text-[13px] font-medium text-accent">
        {t("settings:billing.pricing.savingsHint")}
      </div>
    </div>
  );
}

function PlanColumn({
  tier,
  interval,
  currentTier,
  billingInterval,
  onPlanChangeRequest,
}: {
  tier: PaidTier;
  interval: BillingInterval;
  currentTier: BillingTier;
  billingInterval: "monthly" | "yearly" | null;
  onPlanChangeRequest: (tier: PaidTier) => void;
}) {
  const { t } = useTranslation(["settings"]);
  const pricing = PRICING[tier][interval];

  const effectiveTier = currentTier === "free" ? "essentials" : currentTier;
  const intervalMatches =
    billingInterval === null || interval === billingInterval;
  const isCurrent = effectiveTier === tier && intervalMatches;

  const features = TIER_FEATURES[tier];

  const tierNames: Record<PaidTier, string> = {
    essentials: t("settings:billing.pricing.tierEssentials"),
    professional: t("settings:billing.pricing.tierProfessional"),
    unlimited: t("settings:billing.pricing.tierUnlimited"),
  };

  const tierFeatureLabels: Record<string, string> = {
    audits3: t("settings:billing.pricing.tierFeatures.audits3"),
    audits5: t("settings:billing.pricing.tierFeatures.audits5"),
    auditsUnlimited25Users: t(
      "settings:billing.pricing.tierFeatures.auditsUnlimited25Users",
    ),
    workspace1User1: t("settings:billing.pricing.tierFeatures.workspace1User1"),
    workspace3User5: t("settings:billing.pricing.tierFeatures.workspace3User5"),
    workspacesUnlimited: t(
      "settings:billing.pricing.tierFeatures.workspacesUnlimited",
    ),
    neroKiAssistent: t("settings:billing.pricing.tierFeatures.neroKiAssistent"),
    aiCredits50: t("settings:billing.pricing.tierFeatures.aiCredits50"),
    aiCredits100: t("settings:billing.pricing.tierFeatures.aiCredits100"),
    aiCreditsUnlimited: t(
      "settings:billing.pricing.tierFeatures.aiCreditsUnlimited",
    ),
    gapAnalysisBasic: t(
      "settings:billing.pricing.tierFeatures.gapAnalysisBasic",
    ),
    fullGapAnalysis: t("settings:billing.pricing.tierFeatures.fullGapAnalysis"),
    gapAnalysisAutoFix: t(
      "settings:billing.pricing.tierFeatures.gapAnalysisAutoFix",
    ),
    dashboardPdf: t("settings:billing.pricing.tierFeatures.dashboardPdf"),
    byoa: t("settings:billing.pricing.tierFeatures.byoa"),
    emailSupport: t("settings:billing.pricing.tierFeatures.emailSupport"),
    prioritySupport: t("settings:billing.pricing.tierFeatures.prioritySupport"),
    customerManager: t("settings:billing.pricing.tierFeatures.customerManager"),
    predictiveCompliance: t(
      "settings:billing.pricing.tierFeatures.predictiveCompliance",
    ),
    aiReports: t("settings:billing.pricing.tierFeatures.aiReports"),
  };

  return (
    <div className="relative flex flex-col rounded-xl border border-border bg-card p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="text-[18px] font-semibold text-primary">
            {tierNames[tier]}
          </h3>
          {isCurrent && (
            <Badge variant="status" status="active">
              {t("settings:billing.pricing.activeBadge")}
            </Badge>
          )}
        </div>

        <div className="flex items-baseline gap-1">
          <span className="text-[32px] font-bold text-primary">
            {`\u20AC${pricing.price}`}
          </span>
          <span className="text-[14px] text-secondary">
            {t("settings:billing.pricing.perMonth")}
          </span>
        </div>

        <p className="text-[12px] text-muted mt-1">
          {t("settings:billing.pricing.extraUser", {
            price: `\u20AC${pricing.extraUserPrice}`,
          })}
        </p>
      </div>

      <div className="flex-1 space-y-2.5 mb-6">
        {features.map((feature) => (
          <div key={feature.labelKey} className="flex items-center gap-2.5">
            {feature.included ? (
              <Check size={16} className="text-accent shrink-0" />
            ) : (
              <X size={16} className="text-muted/40 shrink-0" />
            )}
            <span
              className={`text-[13px] ${feature.included ? "text-secondary" : "text-muted/50"}`}
            >
              {tierFeatureLabels[feature.labelKey] ?? feature.labelKey}
            </span>
          </div>
        ))}
      </div>

      <PlanAction
        tier={tier}
        currentTier={currentTier}
        onPlanChangeRequest={onPlanChangeRequest}
      />
    </div>
  );
}

function PlanAction({
  tier,
  currentTier,
  onPlanChangeRequest,
}: {
  tier: PaidTier;
  currentTier: BillingTier;
  onPlanChangeRequest: (tier: PaidTier) => void;
}) {
  const { t } = useTranslation(["settings"]);

  const effectiveTier = currentTier === "free" ? "essentials" : currentTier;

  if (effectiveTier === tier) {
    return (
      <Button variant="secondary" size="sm" className="w-full" disabled>
        {t("settings:billing.pricing.currentPlanButton")}
      </Button>
    );
  }

  const isUpgrade = TIER_ORDER[tier] > TIER_ORDER[effectiveTier];

  return (
    <Button
      variant={isUpgrade ? "primary" : "secondary"}
      size="sm"
      className="w-full"
      onClick={() => onPlanChangeRequest(tier)}
    >
      {isUpgrade
        ? t("settings:billing.pricing.upgradeButton")
        : t("settings:billing.pricing.downgradeButton")}
    </Button>
  );
}
